/**
 * Canali e fornitori: la stessa tabella, una riga per gruppo. Ogni colonna ha
 * la sua «i», e ogni margine apre il suo conto (fatturato − costi = margine).
 * Sul telefono le righe diventano schede.
 */

import type { ReactNode } from "react";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import type { BiEconomia } from "../biApi";
import { calcolabili, eur, intero, marginePerc, n, perc } from "../biTesti";
import { Incerto, InfoTip, type Spiegazione } from "./InfoTip";

export interface Gruppo extends BiEconomia {
  chiave: string;
  nome: string;
  logo?: { source: string; label?: string };
  /** Una riga sotto il nome (es. la regola della commissione). */
  nota?: string;
  spiegaNome?: Spiegazione;
}

const COLONNE: { nome: string; spiega: string }[] = [
  { nome: "Ordini", spiega: "Ordini venduti nel periodo (senza annullati, rimborsati e resi)." },
  {
    nome: "Fatturato netto",
    spiega: "Quanto hanno pagato i clienti, senza IVA. La barra e' la quota sul totale.",
  },
  { nome: "Prodotto", spiega: "Costo pagato al fornitore, senza IVA. Solo ordini calcolabili." },
  {
    nome: "Commissioni",
    spiega: "Quanto trattiene il canale. Solo ordini calcolabili. La «i» sul nome dice la regola.",
  },
  { nome: "Spedizione", spiega: "Costo del corriere, senza IVA. Solo ordini calcolabili." },
  {
    nome: "Margine",
    spiega:
      "Fatturato netto − prodotto − commissioni − spedizione − imballo. Solo ordini calcolabili.",
  },
  { nome: "Margine %", spiega: "Margine ÷ fatturato netto degli ordini calcolabili × 100." },
  {
    nome: "Affidabilita'",
    spiega: "≈ = ordini con un costo stimato. ? = ordini senza margine perche' manca un costo.",
  },
];

function contoMargine(g: Gruppo): Spiegazione {
  return {
    titolo: `Margine ${g.nome}`,
    testo: `Su ${intero(calcolabili(g))} ordini calcolabili di ${intero(g.orders)}.`,
    conto: [
      { etichetta: "Fatturato netto calcolabile", valore: eur(g.net_revenue_computable) },
      { segno: "−", etichetta: "Prodotto", valore: eur(g.product_cost) },
      { segno: "−", etichetta: "Commissioni", valore: eur(g.commission) },
      { segno: "−", etichetta: "Spedizione", valore: eur(g.shipping) },
      { segno: "−", etichetta: "Imballo", valore: eur(g.packaging) },
      { segno: "=", etichetta: "Margine netto", valore: eur(g.margin) },
      { etichetta: "Margine %", valore: perc(marginePerc(g)) },
    ],
    incerto:
      n(g.estimate_count) + n(g.not_computable_count) > 0
        ? `${intero(g.estimate_count)} stimati, ${intero(g.not_computable_count)} senza margine (fuori dal conto).`
        : undefined,
  };
}

function NomeGruppo({ g }: { g: Gruppo }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {g.logo && <MarketplaceLogo source={g.logo.source} label={g.logo.label} size={20} />}
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 font-bold">
          <span className="truncate">{g.nome}</span>
          {g.spiegaNome && <InfoTip spiegazione={g.spiegaNome} />}
        </span>
        {g.nota && <span className="block truncate text-[11.5px] font-semibold">{g.nota}</span>}
      </span>
    </span>
  );
}

function Margine({ g }: { g: Gruppo }) {
  const stimato = n(g.estimate_count) > 0;
  if (calcolabili(g) === 0) return <span className="font-semibold">non calcolabile</span>;
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-bold tabular-nums ${n(g.margin) < 0 ? "text-red" : ""}`}
    >
      {stimato && "≈ "}
      {eur(g.margin)}
      <InfoTip spiegazione={contoMargine(g)} />
    </span>
  );
}

export function TabellaGruppi({
  gruppi,
  etichetta,
}: {
  gruppi: Gruppo[];
  etichetta: string;
}): ReactNode {
  const totale = gruppi.reduce((s, g) => s + n(g.net_revenue), 0) || 1;
  if (gruppi.length === 0) return <p className="text-[13px]">Nessun ordine nel periodo.</p>;
  return (
    <>
      <div className="overflow-x-auto max-md:hidden">
        <table className="w-full min-w-[900px] border-collapse text-[13px]" aria-label={etichetta}>
          <thead>
            <tr className="border-b border-line-2 text-left">
              <th className="pb-2 pr-3 text-[12px] font-bold">{etichetta}</th>
              {COLONNE.map((c) => (
                <th key={c.nome} className="pb-2 pl-3 text-right text-[12px] font-bold">
                  <span className="inline-flex items-center gap-1">
                    {c.nome}
                    <InfoTip spiegazione={{ titolo: c.nome, testo: c.spiega }} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gruppi.map((g) => {
              const quota = (n(g.net_revenue) / totale) * 100;
              return (
                <tr
                  key={g.chiave}
                  className="border-b border-line transition-colors duration-150 hover:bg-paper"
                >
                  <td className="max-w-[260px] py-2.5 pr-3">
                    <NomeGruppo g={g} />
                  </td>
                  <td className="py-2.5 pl-3 text-right tabular-nums">{intero(g.orders)}</td>
                  <td className="py-2.5 pl-3 text-right">
                    <span className="block font-semibold tabular-nums">{eur(g.net_revenue)}</span>
                    <span
                      className="ml-auto mt-1 block h-1 w-[96px] overflow-hidden rounded-full bg-paper-2"
                      aria-hidden="true"
                    >
                      <span
                        className="bi-cresce block h-full rounded-full bg-accent"
                        style={{ width: `${quota}%` }}
                      />
                    </span>
                    <span className="text-[11.5px] font-semibold tabular-nums">
                      {perc(quota, 0)}
                    </span>
                  </td>
                  <td className="py-2.5 pl-3 text-right tabular-nums">{eur(g.product_cost)}</td>
                  <td className="py-2.5 pl-3 text-right tabular-nums">{eur(g.commission)}</td>
                  <td className="py-2.5 pl-3 text-right tabular-nums">{eur(g.shipping)}</td>
                  <td className="py-2.5 pl-3 text-right">
                    <Margine g={g} />
                  </td>
                  <td className="py-2.5 pl-3 text-right font-bold tabular-nums">
                    {perc(marginePerc(g))}
                  </td>
                  <td className="py-2.5 pl-3 text-right">
                    <span className="inline-flex justify-end">
                      {n(g.estimate_count) + n(g.not_computable_count) === 0 ? (
                        <span className="text-[12px] font-bold">certo</span>
                      ) : (
                        <Incerto
                          stimati={n(g.estimate_count)}
                          mancanti={n(g.not_computable_count)}
                          compatto
                        />
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="hidden space-y-2 max-md:block" aria-label={etichetta}>
        {gruppi.map((g) => (
          <li key={g.chiave} className="rounded-[14px] border border-line bg-paper px-3.5 py-3">
            <div className="flex items-start justify-between gap-2">
              <NomeGruppo g={g} />
              <Incerto
                stimati={n(g.estimate_count)}
                mancanti={n(g.not_computable_count)}
                compatto
              />
            </div>
            <dl className="mt-2 grid grid-cols-3 gap-2 text-[13px]">
              <div>
                <dt className="text-[12px] font-semibold">Fatturato</dt>
                <dd className="font-bold tabular-nums">{eur(g.net_revenue)}</dd>
              </div>
              <div>
                <dt className="text-[12px] font-semibold">Margine</dt>
                <dd>
                  <Margine g={g} />
                </dd>
              </div>
              <div>
                <dt className="text-[12px] font-semibold">Ordini · %</dt>
                <dd className="font-bold tabular-nums">
                  {intero(g.orders)} · {perc(marginePerc(g), 0)}
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}
